import { Box, Stack } from '@mantine/core';
import { UserRole } from '@repo/common/enums/UserRole';
import { space } from '@repo/theme/primitives';
import { isEmpty } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { Navigate } from 'react-router';
import type { IWidget, IWidgetSample } from '../../api';
import {
	useDataQualityAccess,
	useSuspenseFeatureAccess,
	useWorkspace,
} from '../../api';
import { EducationModal } from '../../components/EducationModal/EducationModal';
import { appShellStore } from '../../components/SecodaAppShell/store';
import { useUserOnboarding } from '../../hooks/useUserOnboarding';
import { WidgetType } from '../../interfaces';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { useFeatureFlags } from '../../utils/featureFlags';
import EditHomeScreenDrawer from './components/EditHomeScreenDrawer';
import HomepageHeaderAi from './components/HomepageHeaderAi';
import { HomepageEditorOnboarding } from './components/Onboarding';
import WidgetContainer, {
	EmptyWidgetContainer,
} from './components/WidgetContainer';
import { isAISuggestionsWidget } from './components/WidgetContainer/utils';
import {
	COLLECTIONS_WIDGETS,
	HOMEPAGE_ALLOWED_WIDGETS_TO_ADD,
	HOMEPAGE_MAX_WIDTH,
} from './constants';
import { useReportManagement } from './hooks/useReportManagement';
import type {
	UpdatableWidgetKeys,
	UpdatableWidgetValue,
	WidgetRemoveFn,
	WidgetUpdateFn,
} from './types';

function HomePage() {
	const { workspace } = useWorkspace();
	const canShowDataQualityScore = useDataQualityAccess();
	const { secodaAiTasks } = useFeatureFlags();
	const { collectionsAccess, secodaCoreAccess } = useSuspenseFeatureAccess();

	const { hasPermission: canManageHomePage } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Analytics.Manage',
	});

	const { hasPermission: canManageAiSuggestions } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission: 'Workspace.Manage',
	});

	const {
		report,
		widgets,
		addWidget,
		reorderWidgets,
		updateWidget,
		removeWidget,
		updateBackgroundImage,
		isFetching,
	} = useReportManagement({ type: 'homepage' });
	const { dismissViewerOnboardingStepHomescreen } = useUserOnboarding();
	const { aiSuggestionsEnabled } = useFeatureFlags();

	const handleAddWidget = useCallback(
		(widgetSample: IWidgetSample) => {
			addWidget(widgetSample);
			dismissViewerOnboardingStepHomescreen();
		},
		[addWidget, dismissViewerOnboardingStepHomescreen]
	);

	const handleRemoveWidget: WidgetRemoveFn = useCallback(
		(id: IWidget['id']) => () => {
			dismissViewerOnboardingStepHomescreen();
			removeWidget(id)();
		},
		[removeWidget, dismissViewerOnboardingStepHomescreen]
	);

	const handleReorderWidgets = useCallback(
		({ from, to }: { from: number; to: number }) => {
			reorderWidgets({ from, to });
			dismissViewerOnboardingStepHomescreen();
		},
		[reorderWidgets, dismissViewerOnboardingStepHomescreen]
	);

	const handleUpdateWidget: WidgetUpdateFn = useCallback(
		(id: IWidget['id'], key: UpdatableWidgetKeys) =>
			(value: UpdatableWidgetValue | UpdatableWidgetValue[]) => {
				dismissViewerOnboardingStepHomescreen();
				updateWidget(id, key)(value);
			},
		[updateWidget, dismissViewerOnboardingStepHomescreen]
	);

	const widgetSamples = useMemo(
		() =>
			HOMEPAGE_ALLOWED_WIDGETS_TO_ADD.filter((widget) => {
				if (
					widget.type === WidgetType.DATA_QUALITY_SCORE &&
					!canShowDataQualityScore
				) {
					return false;
				}
				if (COLLECTIONS_WIDGETS.includes(widget.type) && !collectionsAccess) {
					return false;
				}

				if (isAISuggestionsWidget(widget.type)) {
					return canManageAiSuggestions && aiSuggestionsEnabled;
				}
				return true;
			}),
		[
			canShowDataQualityScore,
			collectionsAccess,
			canManageAiSuggestions,
			aiSuggestionsEnabled,
		]
	);

	const {
		navBarUI: {
			homePage: { drawerOpen },
		},
		homePageDrawerHandler: { open, close },
	} = appShellStore;

	if (!secodaCoreAccess) {
		return <Navigate to="/ai/new" />;
	}

	return (
		<Stack>
			<Stack
				spacing="5xl"
				py="6xl"
				px="xl"
				mx="auto"
				w="100%"
				maw={HOMEPAGE_MAX_WIDTH + 2 * space[6]}
			>
				<HomepageHeaderAi />
				<Stack>
					{!workspace?.onboarding_completed && canManageHomePage && (
						<HomepageEditorOnboarding />
					)}
					<Box>
						{isEmpty(widgets) && !isFetching ? (
							<EmptyWidgetContainer onCustomizeButtonClick={open} />
						) : (
							<WidgetContainer
								widgets={widgets}
								onReorderWidgets={handleReorderWidgets}
								onUpdateWidget={handleUpdateWidget}
								onRemoveWidget={handleRemoveWidget}
							/>
						)}
					</Box>
				</Stack>
				<EditHomeScreenDrawer
					widgetSamples={widgetSamples}
					opened={drawerOpen}
					onClose={close}
					backgroundImage={report?.background_image}
					onAddWidget={handleAddWidget}
					onBackgroundImageChange={updateBackgroundImage}
					coverImageEnabled={false}
				/>
				{secodaAiTasks && (
					<EducationModal modalKey="education-modal-of-secoda-ai-agents" />
				)}
			</Stack>
		</Stack>
	);
}

const HomePageComponent = observer(HomePage);
export default HomePageComponent;
